export default class Toast {
    private static container: HTMLDivElement;

    private static initializeContainer() {
        if (!this.container) {
            this.container = document.createElement('div');
            // Tailwind classes for the container
            this.container.className = [
                'fixed',
                'top-5',      // 20px
                'right-[10%] sm:right-8',
                'left-[10%] sm:left-8',
                'z-[1000]',   // High z-index
                'flex',
                'flex-col',
                'items-center', // Toasts are centered within this container
                'gap-2.5'     // 10px
            ].join(' ');
            document.body.appendChild(this.container);
        }
    }

    private static createToastElement(message: string, type: 'success' | 'error' | 'loading', duration: number = 3000) {
        const toast = document.createElement('div');
        toast.textContent = message;

        // Base Tailwind classes for the toast element
        const baseClasses = [
            'max-w-md',   // 24rem
            'w-fit',
            'mx-auto',    // For centering within the items-center container
            'py-2.5',     // 10px
            'px-5',       // 20px
            'rounded-full', 
            'text-white',
            'text-center',
            'text-base',
            'shadow-md',
            'border-wm-lg',
            'transition-all',
            'duration-300',
            'ease-in-out',
            'overflow-hidden' // Added for smooth height collapse
        ];

        // Type-specific classes
        let typeClasses: string[] = [];
        if (type === 'success') {
            typeClasses = ['bg-green-500'];
        } else if (type === 'error') {
            typeClasses = ['bg-red-500'];
        } else if (type === 'loading') {
            typeClasses = ['bg-blue-500', 'flex', 'items-center', 'gap-2'];
            const spinner = document.createElement('div');
            spinner.className = 'animate-spin rounded-full h-5 w-5 border-b-2 border-white';
            toast.appendChild(spinner);
            toast.textContent = ''; // Clear the text content for loading state
            toast.appendChild(spinner);
            const loadingText = document.createElement('span');
            loadingText.textContent = message;
            loadingText.className = 'text-white';
            toast.appendChild(loadingText);
        }


        // Initial animation state (slid up, faded out, scaled down)
        const initialAnimationClasses = [
            'opacity-0',
            'transform',
            '-translate-y-5',
            'scale-90',
            'max-h-[500px]'   // Initial max-height for collapse transition
        ];

        toast.className = [...baseClasses, ...typeClasses, ...initialAnimationClasses].join(' ');
        this.container.appendChild(toast);

        // Trigger slide-in animation shortly after appending
        setTimeout(() => {
            toast.classList.remove('opacity-0', '-translate-y-5', 'scale-90', 'max-h-[500px]');
            toast.classList.add('opacity-100', 'translate-y-0', 'scale-100');
        }, 50);
        return toast;
    }

    static loading(message: string, duration: number = 3000) {
        return this.createToastElement(message, 'loading', duration);
    }

    static success(message: string, duration: number = 3000) {
        this.initializeContainer();
        let toast = this.createToastElement(message, 'success', duration);
        this.removeToast(toast, duration)
    }

    static removeToast(toast: HTMLDivElement, duration: number = 0) {
        // Automatically dismiss the toast
        setTimeout(() => {
            // Start slide-out and collapse animation
            toast.classList.remove('opacity-100', 'translate-y-0', 'scale-100');
            toast.classList.add(
                'opacity-0',
                '-translate-y-5',
                'scale-90',
                '!max-h-0',        // Collapse height
                '!py-0',            // Collapse padding
                'border-none'
            );

            // Remove the element after the animation completes
            setTimeout(() => {
                if (toast.parentNode) {
                    toast.parentNode.removeChild(toast);
                }
            }, 300); // Matches transition duration (300ms)
        }, duration); // Toast visible duration
    }

    static error(message: string, duration: number = 3000) {
        this.initializeContainer();
        let toast = this.createToastElement(message, 'error', duration);
        this.removeToast(toast, duration)
    }
}

window.Toast = Toast;